import { useCallback, useEffect, useRef, useState } from "react";

import { resolveSubmissionStatus } from "./submissionStatus";
import { useStatusClient } from "./useStatusClient";
import type { LeadResult } from "./submit";

/** `checking` until the server has been asked; `retry` when a submission
 * still awaiting confirmation could not be given a usable grant. */
export type SubmissionStatusPhase = "checking" | "ready" | "retry";

/**
 * Reads the stored submission's live status once per visit (and again on
 * `retry`), handing any result it changes to `onResult`. Nothing is asked for
 * a result without a submission id.
 */
export function useSubmissionStatus(
  result: LeadResult | null,
  onResult: (result: LeadResult) => void,
): { phase: SubmissionStatusPhase; retry: () => void } {
  const submissionId = result?.submissionId ?? null;
  const [phase, setPhase] = useState<SubmissionStatusPhase>(
    submissionId === null ? "ready" : "checking",
  );
  const [attempt, setAttempt] = useState(0);
  const client = useStatusClient();
  const resultRef = useRef(result);
  resultRef.current = result;
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    const current = resultRef.current;
    if (submissionId === null || current === null) {
      setPhase("ready");
      return;
    }
    let cancelled = false;
    setPhase("checking");
    void resolveSubmissionStatus(current, client).then((outcome) => {
      if (cancelled) return;
      if (outcome.kind !== "unchanged") onResultRef.current(outcome.result);
      setPhase(outcome.kind === "retry" ? "retry" : "ready");
    });
    return () => {
      cancelled = true;
    };
  }, [submissionId, attempt, client]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { phase, retry };
}
